import { VocalLib, type PitchFrame } from '@tunasong/music-lib'

/**
 * One take: open the inputs, give the room a moment, count in, click through the bars, stop.
 *
 * Every side effect is injected, so the two orderings a broken capture would break silently are
 * unit-testable without an audio graph:
 *
 *  1. **The microphone opens before the lead-in wait.** The voicing gate measures the noise floor
 *     from the first 500 ms of the window (vocal spec §5.1). Start the click inside that window and
 *     the floor is measured from the click, after which nothing the player does clears it.
 *  2. **The click starts after the lead-in and never before it** - and it is never muted, which is
 *     the one place this differs from the Pitch Trainer's window. There the reference had to be
 *     silenced so speaker bleed could not be scored as singing; here the click IS the timebase the
 *     performance is measured against and has to be audible for the whole take. That is precisely
 *     why audio capture requires headphones (§5): a click out of a speaker would be captured,
 *     segmented, and written into the melody as notes, on the beat, every beat.
 *
 * `wait` is injected for a second reason beyond testing: the host's implementation resolves early
 * when the user presses Stop, which is how a take ends before its last bar without this file knowing
 * anything about buttons.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

export type TakePhase = 'lead-in' | 'count-in' | 'take' | 'quantizing'

export interface TakeDeps {
  /** Silence any melody playback. A reference roll still sounding would be captured. */
  stopPlayback: () => void
  /** Fire the whole click track, count-in included. Fire-and-forget; the waits own the clock. */
  playClick: () => void
  /** Injected so tests need no fake timers, and so Stop can resolve a wait early. */
  wait: (ms: number) => Promise<void>
  /** Audio takes only. Resolves to an unsubscribe. */
  startFrames?: (onFrame: (frame: PitchFrame) => void) => Promise<() => void>
  /** Audio takes only. Resolves to a stop that resolves to the recorded blob. */
  startRecording?: () => Promise<() => Promise<Blob | null>>
  onPhase?: (phase: TakePhase) => void
  /** Every frame as it arrives, re-based, for the live meter. The same frames the take collects. */
  onFrame?: (frame: PitchFrame) => void
}

export interface TakeOptions {
  /** Room tone before the count-in. `AUDIO_LEAD_IN_MS` for audio, 0 for MIDI. */
  leadInMs: number
  countInMs: number
  takeMs: number
  tailMs: number
}

export interface TakeResult {
  frames: PitchFrame[]
  blob: Blob | null
  /** The take's beat 1, in ms on the frames' (re-based) clock. */
  t0Ms: number
}

/**
 * Room tone before the count-in on an audio take.
 *
 * The floor is the median RMS of the first `VocalLib.noiseFloorWindowMs` (500 ms), so the lead-in
 * has to cover at least that or the floor is measured partly from the click. 100 ms of margin covers
 * the gap between `startFrames` resolving and the first frame arriving, which is up to one analysis
 * hop (21 ms) plus the worklet's own start-up.
 */
export const AUDIO_LEAD_IN_MS = VocalLib.noiseFloorWindowMs + 100

/** Four bars is a phrase - long enough to be a melody, short enough to nail in one take. */
export const DEFAULT_BARS = 4

/**
 * Sixteen bars. Past this a take is a section rather than a melody, and the review's roll stops
 * being readable at one screen width. §5 sets this cap.
 */
export const MAX_BARS = 16

/**
 * The longest free take, in ms. A CONSTANT §11 DID NOT GIVE.
 *
 * A free take has no bar count to size it - that is the point - so it needs a ceiling of its own.
 * A minute is the same judgement `MAX_BARS` makes in beats: past this a take is a section rather
 * than a melody, and the review's roll stops being readable at one screen width. Stop is how a free
 * take normally ends; this is only the backstop for a user who walks away from an open microphone.
 */
export const FREE_TAKE_MAX_MS = 60000

/**
 * Listening past the last bar line. A player releases the final note after the beat, and a MIDI
 * noteoff that never arrives inside the window turns a held note into a note with no length.
 */
export const TAKE_TAIL_MS = 1000

export const runMelodyTake = async (deps: TakeDeps, options: TakeOptions): Promise<TakeResult> => {
  const { stopPlayback, playClick, wait, startFrames, startRecording, onPhase, onFrame } = deps
  const { leadInMs, countInMs, takeMs, tailMs } = options

  stopPlayback()

  const frames: PitchFrame[] = []
  /**
   * The worklet timestamps frames from its own construction, not from this take, so on a second take
   * the first frame arrives tens of seconds in and `noiseFloorRms` - which selects `time < 0.5` -
   * would find no lead-in at all.
   */
  let origin: number | null = null
  let latestMs = 0

  const stopFrames = await startFrames?.(frame => {
    origin = origin ?? frame.time
    const rebased = { ...frame, time: frame.time - origin }
    latestMs = rebased.time * 1000
    frames.push(rebased)
    onFrame?.(rebased)
  })
  const stopRecording = await startRecording?.()

  let blob: Blob | null = null
  let t0Ms = countInMs
  try {
    onPhase?.('lead-in')
    await wait(leadInMs)

    /*
     * The frame clock, not the wall clock: the take's beat 1 is `countInMs` after the click starts,
     * and the click starts now. Reading the latest frame time rather than assuming the lead-in wait
     * equalled the frames' elapsed time keeps the two clocks from drifting apart.
     */
    t0Ms = latestMs + countInMs
    onPhase?.('count-in')
    playClick()

    onPhase?.('take')
    await wait(countInMs + takeMs + tailMs)
  } finally {
    stopFrames?.()
    blob = (await stopRecording?.()) ?? null
  }

  onPhase?.('quantizing')
  return { frames, blob, t0Ms }
}
